import { useEffect, useRef, type DependencyList, type RefObject } from 'react';

/** Applied to any element inside the chart that scrolls horizontally so native bars don’t cover grid rows. */
export const SCHEDULE_GANTT_HIDE_NATIVE_HSCROLL_CLASS = 'schedule-gantt-hide-native-hscroll';

/**
 * SVAR often puts the timeline horizontal scroller on a nested div, not only `.wx-theme` / `.wx-gantt`.
 * Tag every horizontally-overflowing node so CSS can hide its scrollbar; mirrored bar + scrollLeft stay in sync.
 */
function refreshScheduleGanttHiddenNativeHScrollTargets(viewport: HTMLElement): void {
  const cls = SCHEDULE_GANTT_HIDE_NATIVE_HSCROLL_CLASS;
  const next = new Set<HTMLElement>();
  const all = viewport.querySelectorAll<HTMLElement>('*');
  for (let i = 0; i < all.length; i += 1) {
    const el = all[i];
    if (el.scrollWidth > el.clientWidth + 1) {
      next.add(el);
    }
  }
  const tagged = viewport.querySelectorAll<HTMLElement>(`.${cls}`);
  for (let i = 0; i < tagged.length; i += 1) {
    const el = tagged[i];
    if (!next.has(el)) el.classList.remove(cls);
  }
  next.forEach((el) => el.classList.add(cls));
}

/** True when the element can actually be panned horizontally (overflow-x auto/scroll); a node with
 * `overflow-x: visible` reports overflow but silently ignores `scrollLeft`, so it must not be chosen. */
function canScrollHorizontally(el: HTMLElement): boolean {
  const overflowX = getComputedStyle(el).overflowX;
  return overflowX === 'auto' || overflowX === 'scroll';
}

/**
 * Element that owns horizontal panning — often a nested timeline pane, not `.wx-theme` / `.wx-gantt`.
 * Prefer the scrollable node with the largest (scrollWidth - clientWidth) so scrollLeft sync matches the real bar.
 */
export function getScheduleGanttHorizontalScrollTarget(viewport: HTMLElement | null): HTMLElement | null {
  if (!viewport) return null;
  let best: HTMLElement = viewport;
  let bestOverflow = viewport.scrollWidth - viewport.clientWidth;
  const all = viewport.querySelectorAll<HTMLElement>('*');
  for (let i = 0; i < all.length; i += 1) {
    const el = all[i];
    const ow = el.scrollWidth - el.clientWidth;
    if (ow > bestOverflow && canScrollHorizontally(el)) {
      bestOverflow = ow;
      best = el;
    }
  }
  return bestOverflow > 1 ? best : viewport;
}

/**
 * Width of the mirrored scrollbar's inner spacer. The fake bar spans the full wrapper (grid columns +
 * timeline), so its scrollable range must equal the target's overflow: `barClientWidth + overflow`.
 * Using the target's raw scrollWidth would leave the bar short by the grid width (can't reach the right
 * edge) and hide the thumb until overflow exceeds the grid width.
 */
export function computeScheduleGanttHScrollSpacerWidth(
  targetScrollWidth: number,
  targetClientWidth: number,
  barClientWidth: number
): number {
  return barClientWidth + Math.max(0, targetScrollWidth - targetClientWidth);
}

/**
 * Bottom horizontal scrollbar (fixed under the chart) + Shift/trackpad horizontal wheel,
 * synced with the real horizontal scroll owner (usually the viewport).
 */
export function useScheduleGanttChartScroll(
  viewportRef: RefObject<HTMLDivElement | null>,
  bottomBarRef: RefObject<HTMLDivElement | null>,
  bottomInnerRef: RefObject<HTMLDivElement | null>,
  deps: DependencyList
): void {
  const syncingRef = useRef(false);

  useEffect(() => {
    const viewport = viewportRef.current;
    const bar = bottomBarRef.current;
    const inner = bottomInnerRef.current;
    if (!viewport || !bar || !inner) return;

    const getT = () => getScheduleGanttHorizontalScrollTarget(viewport) ?? viewport;

    const syncBarFromTarget = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      bar.scrollLeft = getT().scrollLeft;
      syncingRef.current = false;
    };

    const syncTargetFromBar = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      getT().scrollLeft = bar.scrollLeft;
      syncingRef.current = false;
    };

    const onWheel = (e: WheelEvent) => {
      const t = getT();
      if (t.scrollWidth <= t.clientWidth) return;
      const useHorizontal = e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY);
      if (!useHorizontal) return;
      e.preventDefault();
      const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
      t.scrollLeft += delta;
    };

    /** Nested timeline may replace the horizontal scroll owner after SVAR renders — rebind listener + ResizeObserver. */
    let horizontalScrollInner: HTMLElement | null = null;
    /** Content node inside the scroller — observed separately because a scroller's own box doesn't resize when its content grows. */
    let horizontalScrollContent: HTMLElement | null = null;

    let rafSync = 0;
    let updateInnerWidth: () => void;

    const scheduleSync = () => {
      if (rafSync) cancelAnimationFrame(rafSync);
      rafSync = requestAnimationFrame(() => {
        rafSync = 0;
        updateInnerWidth();
        syncBarFromTarget();
      });
    };

    const ro = new ResizeObserver(() => {
      scheduleSync();
    });

    const refreshHorizontalScrollBindings = () => {
      const t = getT();
      const nextInner = t !== viewport ? t : null;
      if (horizontalScrollInner === nextInner) return;
      if (horizontalScrollInner) {
        horizontalScrollInner.removeEventListener('scroll', syncBarFromTarget);
        ro.unobserve(horizontalScrollInner);
      }
      if (horizontalScrollContent) {
        ro.unobserve(horizontalScrollContent);
        horizontalScrollContent = null;
      }
      horizontalScrollInner = nextInner;
      if (horizontalScrollInner) {
        horizontalScrollInner.addEventListener('scroll', syncBarFromTarget, { passive: true });
        ro.observe(horizontalScrollInner);
        // Observe the content node too: the scroller's own box doesn't resize when SVAR grows its content.
        const content = horizontalScrollInner.firstElementChild as HTMLElement | null;
        if (content) {
          horizontalScrollContent = content;
          ro.observe(content);
        }
      }
    };

    updateInnerWidth = () => {
      refreshScheduleGanttHiddenNativeHScrollTargets(viewport);
      const t = getT();
      const w = computeScheduleGanttHScrollSpacerWidth(t.scrollWidth, t.clientWidth, bar.clientWidth);
      inner.style.width = `${w}px`;
      inner.style.height = '1px';
      refreshHorizontalScrollBindings();
    };

    updateInnerWidth();
    syncBarFromTarget();

    viewport.addEventListener('scroll', syncBarFromTarget, { passive: true });
    bar.addEventListener('scroll', syncTargetFromBar, { passive: true });
    viewport.addEventListener('wheel', onWheel, { passive: false });

    ro.observe(viewport);
    ro.observe(bar);

    const mo = new MutationObserver(() => {
      scheduleSync();
    });
    mo.observe(viewport, { subtree: true, childList: true, attributes: true });

    // SVAR often finalizes timeline widths a few frames after mount. Re-measure each frame until the
    // scroll width stops changing (or we hit the cap) so the spacer is never left stale/short.
    let settleRaf = 0;
    let settleFrames = 0;
    let lastScrollWidth = -1;
    let stableFrames = 0;
    const settle = () => {
      updateInnerWidth();
      syncBarFromTarget();
      const width = getT().scrollWidth;
      stableFrames = width === lastScrollWidth ? stableFrames + 1 : 0;
      lastScrollWidth = width;
      settleFrames += 1;
      if (stableFrames < 3 && settleFrames < 60) {
        settleRaf = requestAnimationFrame(settle);
      } else {
        settleRaf = 0;
      }
    };
    settleRaf = requestAnimationFrame(settle);

    return () => {
      if (rafSync) cancelAnimationFrame(rafSync);
      if (settleRaf) cancelAnimationFrame(settleRaf);
      viewport.removeEventListener('scroll', syncBarFromTarget);
      if (horizontalScrollInner) {
        horizontalScrollInner.removeEventListener('scroll', syncBarFromTarget);
      }
      bar.removeEventListener('scroll', syncTargetFromBar);
      viewport.removeEventListener('wheel', onWheel);
      ro.disconnect();
      mo.disconnect();
    };
  }, deps);
}
